import { AfterViewInit, Component, ElementRef, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { Subscription } from 'rxjs';
import {
  SuperbillCode,
  SuperbillMatchKey,
  SuperbillStore,
  SuperbillService,
} from '@services/superbill.service';
import { NotificationService } from '@services/notification.service';
import { AuthService } from '@services/auth.service';

@Component({
  selector: 'app-superbill-setup',
  templateUrl: './superbill-setup.component.html',
  styleUrls: ['./superbill-setup.component.css'],
})
export class SuperbillSetupComponent implements OnInit, AfterViewInit, OnDestroy {
  @ViewChild('preview') previewCanvas?: ElementRef<HTMLCanvasElement>;

  codes: SuperbillCode[] = [];
  matchKeys: SuperbillMatchKey[] = [];
  stores: SuperbillStore[] = [];
  templateName = '';
  templateUpdated = '';
  templateBlob: Blob | null = null;
  currentStoreName = '';
  saving = false;
  private storeId: string | number = '';
  private viewReady = false;
  private storeSub?: Subscription;
  private pdfjsPromise: Promise<any> | null = null;
  draft: SuperbillCode = {
    displayName: '',
    insuranceCode: '',
    matchKey: 'FRAME',
    active: true,
  };

  constructor(
    private superbillService: SuperbillService,
    private notification: NotificationService,
    private auth: AuthService
  ) {}

  ngOnInit(): void {
    this.storeSub = this.auth.getStoreObjectMessage().subscribe((store) => {
      const id = store && store._id;
      if (!id) {
        return;
      }
      const changed = String(this.storeId) !== String(id);
      this.storeId = id;
      this.currentStoreName = store.storeName || '';
      if (changed) {
        this.templateBlob = null;
      }
      this.load();
      if (this.viewReady) {
        this.loadPreview();
      }
    });
  }

  ngAfterViewInit(): void {
    this.viewReady = true;
    if (this.storeId) {
      this.loadPreview();
    }
  }

  ngOnDestroy(): void {
    if (this.storeSub) {
      this.storeSub.unsubscribe();
    }
  }

  load(): void {
    if (!this.storeId) {
      return;
    }
    this.superbillService.getSetup(this.storeId).subscribe({
      next: (setup) => {
        this.codes = setup.codes || [];
        this.matchKeys = setup.matchKeys || [];
        this.stores = (setup.stores || []).map((store) => ({
          ...store,
          dispensaryName: store.dispensaryName || 'OPTICAL DISPENSARY',
        }));
        if (setup.template?.name) {
          this.templateName = setup.template.name;
        }
        this.templateUpdated = this.formatUpdated(setup.template?.updatedAt);
        if (!this.draft.matchKey && this.matchKeys.length) {
          this.draft.matchKey = this.matchKeys[0].key;
        }
      },
      error: () => {
        this.notification.show('error', 'Could not load superbill setup');
      },
    });
  }

  add(): void {
    if (!this.draft.displayName.trim() || !this.draft.matchKey) {
      return;
    }
    const sortOrder = (this.codes.length + 1) * 10;
    this.saveCode({ ...this.draft, sortOrder });
    this.draft = {
      displayName: '',
      insuranceCode: '',
      matchKey: this.draft.matchKey,
      active: true,
    };
  }

  saveCode(code: SuperbillCode): void {
    this.saving = true;
    this.superbillService.saveCode(code).subscribe({
      next: () => {
        this.saving = false;
        this.notification.show('success', 'Superbill code saved');
        this.load();
      },
      error: (err) => {
        this.saving = false;
        this.notification.show('error', err?.error?.message || 'Could not save superbill code');
      },
    });
  }

  remove(code: SuperbillCode): void {
    if (!code._id) {
      return;
    }
    this.superbillService.removeCode(code._id).subscribe({
      next: () => {
        this.notification.show('success', 'Superbill code removed');
        this.load();
      },
      error: () => {
        this.notification.show('error', 'Could not remove superbill code');
      },
    });
  }

  onTemplateSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    const file = input.files && input.files[0];
    input.value = '';
    if (!file) {
      return;
    }
    const looksPdf =
      file.type === 'application/pdf' || file.name.toLowerCase().endsWith('.pdf');
    if (!looksPdf) {
      this.notification.show('error', 'Upload a PDF file');
      return;
    }
    if (!this.storeId) {
      this.notification.show('error', 'Choose a store before uploading a template');
      return;
    }
    this.saving = true;
    this.superbillService.uploadTemplate(file, this.storeId).subscribe({
      next: (template) => {
        this.saving = false;
        this.templateName = template?.name || file.name;
        this.templateUpdated = this.formatUpdated(template?.updatedAt);
        this.notification.show('success', 'Superbill template uploaded for ' + (this.currentStoreName || 'this store'));
        this.loadPreview();
      },
      error: (err) => {
        this.saving = false;
        this.notification.show('error', err?.error?.message || 'Could not upload the template');
      },
    });
  }

  openTemplate(): void {
    if (this.templateBlob) {
      this.superbillService.openPdf(this.templateBlob, false);
    }
  }

  private loadPreview(): void {
    if (!this.storeId) {
      return;
    }
    const requested = this.storeId;
    this.superbillService.templatePdf(requested).subscribe({
      next: (blob) => {
        if (String(requested) !== String(this.storeId)) {
          return;
        }
        this.templateBlob = blob;
        this.drawPreview(blob);
      },
      error: () => {
        this.notification.show('error', 'Could not load the superbill template');
      },
    });
  }

  private drawPreview(blob: Blob): void {
    blob
      .arrayBuffer()
      .then((buffer) => this.loadPdfJs().then((pdfjs) => pdfjs.getDocument({ data: new Uint8Array(buffer) }).promise))
      .then((pdf) => pdf.getPage(1))
      .then((page) => {
        const canvas = this.previewCanvas?.nativeElement;
        const context = canvas && canvas.getContext('2d');
        if (!canvas || !context) {
          return null;
        }
        const viewport = page.getViewport({ scale: 0.45 });
        canvas.width = viewport.width;
        canvas.height = viewport.height;
        return page.render({ canvasContext: context, viewport }).promise;
      })
      .catch(() => {
        this.notification.show('error', 'Could not preview the template');
      });
  }

  private loadPdfJs(): Promise<any> {
    const win = window as any;
    if (win.pdfjsLib) {
      win.pdfjsLib.GlobalWorkerOptions.workerSrc = '/assets/pdfjs/pdf.worker.min.js';
      return Promise.resolve(win.pdfjsLib);
    }
    if (!this.pdfjsPromise) {
      this.pdfjsPromise = new Promise((resolve, reject) => {
        const script = document.createElement('script');
        script.src = '/assets/pdfjs/pdf.min.js';
        script.onload = () => {
          if (!win.pdfjsLib) {
            reject(new Error('PDF preview could not start'));
            return;
          }
          win.pdfjsLib.GlobalWorkerOptions.workerSrc = '/assets/pdfjs/pdf.worker.min.js';
          resolve(win.pdfjsLib);
        };
        script.onerror = () => reject(new Error('PDF preview could not start'));
        document.body.appendChild(script);
      });
    }
    return this.pdfjsPromise;
  }

  private formatUpdated(value?: string | null): string {
    if (!value) {
      return '';
    }
    const date = new Date(value);
    if (isNaN(date.getTime())) {
      return '';
    }
    return date.toLocaleDateString('en-US');
  }

  saveStore(store: SuperbillStore): void {
    this.saving = true;
    this.superbillService.saveStore(store).subscribe({
      next: (saved) => {
        this.saving = false;
        const index = this.stores.findIndex((row) => row._id === store._id);
        if (saved && index >= 0) {
          this.stores[index] = {
            ...saved,
            dispensaryName: saved.dispensaryName || 'OPTICAL DISPENSARY',
          };
        }
        this.notification.show('success', 'Store superbill information saved');
        if (String(store._id) === String(this.storeId)) {
          this.loadPreview();
        }
      },
      error: () => {
        this.saving = false;
        this.notification.show('error', 'Could not save store superbill information');
      },
    });
  }
}
